/**
 * How long to keep waiting for the async render before falling back to the email/chat message.
 *
 * This must OUTLAST the async render, or the poll is decoration: `asyncPDFUrl` gets a 295s
 * in-browser budget (§4 of docs/flows/pdf-generation.md) plus the S3 upload, so at the old 90s
 * the in-session wait spanned only ~110s counting the failed sync attempt. Every photo- and
 * video-heavy job landed in the uncovered band and always got the "we'll email you a link" message,
 * even though the render finished fine a minute later (ENG-1080 QA). Don't lower this without
 * lowering the async budget first.
 */
export const DEFAULT_PDF_WAIT_TIMEOUT_MS = 300 * 1000
/** How often to re-probe the cache bucket while waiting. */
export const DEFAULT_PDF_WAIT_INTERVAL_MS = 3 * 1000

export interface WaitForPdfArgs {
  /** The S3 cache URL the async render writes to (same key the pre-flight check uses). */
  url: string
  /** Existence probe. Resolves the url when present, `false` otherwise, and never throws. */
  urlExists: (url: string) => Promise<string | false>
  /** Total time to keep waiting, in ms. */
  timeoutMs?: number
  /** Delay between probes, in ms. */
  intervalMs?: number
  /** Aborts the wait — e.g. the dialog was cancelled or unmounted. */
  signal?: AbortSignal
  /** Injectable clock, for tests. */
  now?: () => number
  /** Injectable delay, for tests. */
  sleep?: (ms: number) => Promise<void>
}

/**
 * Wait for a PDF to land in the cache bucket.
 *
 * The sync render can't exceed ~20s (28s Lambda / 30s API Gateway), so a photo-heavy work order
 * always fails it and hands off to the `asyncPDFUrl` task, which re-renders with a 5-minute budget
 * and writes to the *same* S3 key. Rather than sending the user off to wait for an email, poll that
 * key: the render usually lands in well under a minute, so the download can finish in-session
 * (#22017). The email/chat fallback still fires from the task either way, so a timeout here is
 * degraded UX, never a lost PDF.
 *
 * @returns `true` once the PDF exists, `false` on timeout or abort.
 */
export async function waitForPdf({
  url,
  urlExists,
  timeoutMs = DEFAULT_PDF_WAIT_TIMEOUT_MS,
  intervalMs = DEFAULT_PDF_WAIT_INTERVAL_MS,
  signal,
  now = () => Date.now(),
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
}: WaitForPdfArgs): Promise<boolean> {
  const deadline = now() + timeoutMs

  // Probe immediately: the async task may already have finished while the sync attempt was
  // timing out.
  while (!signal?.aborted) {
    if (await urlExists(url)) return true

    // Re-check both conditions before sleeping so we never wait past the deadline (or past an
    // abort that landed during the probe).
    if (signal?.aborted || now() + intervalMs > deadline) return false

    // Intentionally a plain sleep rather than an abort-aware one: an abort landing mid-sleep is
    // only noticed once it resolves, so resolving can lag cancellation by up to `intervalMs`. That
    // is fine here — the loop check above means no further probe is issued, the caller has already
    // closed the dialog and ignores the result, and nothing holds a resource meanwhile. Don't
    // optimize this into an abort listener without a reason; the extra wiring buys nothing.
    await sleep(intervalMs)
  }

  return false
}

export default waitForPdf
